// src/services/articlesPhotoServices.js
import axios from "axios";

// Compat Vite & CRA
const _base =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_URL) ||
  process.env.REACT_APP_API_URL ||
  "";

const baseUrl = _base.replace(/\/$/, "");

// 🔹 Local (sharp + WebP ≤600) — routes non /api
const articlesLocalBase = `${baseUrl}/articles`;

// 🔹 Rétro-compat Cloudinary (si encore utilisé) — routes /api
const articlesApiBase = `${baseUrl}/api/articles`;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Upload/Remplace la photo d’un Article (local sharp).
 * - Côté serveur : MASTER (≤1600) + DISPLAY (≤600, fit: inside), auto-rotate EXIF.
 * - `format` (webp|jpg|png) en query — défaut "webp".
 * - Réponse: { id, photo_url } (chemin /uploads/…)
 *
 * @param {number|string} articleId
 * @param {File|Blob} file
 * @param {Object} options
 * @param {"webp"|"jpg"|"png"} [options.format="webp"]
 * @param {function} [options.onUploadProgress]
 * @returns {Promise<{id:number, photo_url:string}>}
 */
export const uploadArticlePhoto = async (articleId, file, options = {}) => {
  if (!articleId) throw new Error("articleId requis");
  if (!file) throw new Error("file requis");

  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  // ⚠️ Côté serveur: memoryUpload.single("file")
  form.append("file", file);

  try {
    const { data } = await axios.post(
      `${articlesLocalBase}/${encodeURIComponent(
        articleId
      )}/photo?format=${encodeURIComponent(format)}`,
      form,
      {
        headers: {
          ...authHeaders(),
          // axios gère le Content-Type multipart
        },
        onUploadProgress,
      }
    );
    return data; // { id, photo_url }
  } catch (error) {
    console.error("[uploadArticlePhoto] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * Recadre/zoome la photo d’un Article existant (local sharp).
 * - Si 'src' est omis, le serveur dérive le MASTER depuis la photo actuelle.
 * - x,y,width,height en px par rapport au MASTER.
 * - outW/outH → resize cover(outW,outH) ; sinon sortie ≤600 (inside).
 * - Réponse: { id, photo_url } (nouvelle display -600).
 *
 * @param {number|string} articleId
 * @param {Object} crop
 * @param {number} crop.x
 * @param {number} crop.y
 * @param {number} crop.width
 * @param {number} crop.height
 * @param {"webp"|"jpg"|"png"} [crop.format]
 * @param {string} [crop.src]     - /uploads/... si tu veux recadrer une autre source
 * @param {number} [crop.outW]    - largeur de sortie (cover)
 * @param {number} [crop.outH]    - hauteur de sortie (cover)
 * @returns {Promise<{id:number, photo_url:string}>}
 */
export const cropArticlePhoto = async (articleId, crop = {}) => {
  if (!articleId) throw new Error("articleId requis");

  const { x, y, width, height, format, src, outW, outH } = crop;
  const nums = [x, y, width, height];
  if (nums.some((v) => typeof v !== "number" || Number.isNaN(v))) {
    throw new Error("x, y, width, height numériques requis");
    }

  const payload = { x, y, width, height };
  if (format) payload.format = format;
  if (src) payload.src = src;
  if (typeof outW === "number") payload.outW = outW;
  if (typeof outH === "number") payload.outH = outH;

  try {
    const { data } = await axios.post(
      `${articlesLocalBase}/${encodeURIComponent(articleId)}/crop`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      }
    );
    return data; // { id, photo_url }
  } catch (error) {
    console.error("[cropArticlePhoto] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * (Optionnel) Upload via Cloudinary — rétro-compat.
 * - Route : PATCH /api/articles/:id/photo
 * - Champ: "image"
 * - Réponse: dépend de articleController.updateArticlePhoto (legacy)
 *
 * @param {number|string} articleId
 * @param {File|Blob} file
 * @param {Object} options
 * @param {"webp"|"jpg"|"png"} [options.format="webp"]
 * @param {function} [options.onUploadProgress]
 * @returns {Promise<any>}
 */
export const uploadArticlePhotoCloudinary = async (
  articleId,
  file,
  options = {}
) => {
  if (!articleId) throw new Error("articleId requis");
  if (!file) throw new Error("file requis");

  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  // ⚠️ Route Cloudinary: upload("articles").single("image")
  form.append("image", file);

  try {
    const { data } = await axios.patch(
      `${articlesApiBase}/${encodeURIComponent(
        articleId
      )}/photo?format=${encodeURIComponent(format)}`,
      form,
      {
        headers: {
          ...authHeaders(),
        },
        onUploadProgress,
      }
    );
    return data;
  } catch (error) {
    console.error("[uploadArticlePhotoCloudinary] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * Helper: renvoie une URL exploitable dans <img src=...>
 * - Si commence par /uploads/ => préfixe avec baseUrl (API)
 * - Sinon renvoie tel quel (ex: Cloudinary https://…)
 *
 * @param {string} photoUrl
 * @returns {string}
 */
export const toAbsoluteArticlePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (photoUrl.startsWith("/uploads/")) {
    return `${baseUrl}${photoUrl}`;
  }
  return photoUrl;
};
